/**
 * ChannelTransparencyTable.js
 *
 * Componente de tabela hierárquica para Channel Transparency
 * Baseado no componente Pnl.js com estrutura similar
 * Suporta ordenação por clique no header das colunas
 */

import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";

const styles = () => ({
  plWrapper: {
    position: "relative",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: "5px",
    overflow: "hidden",
  },
  columnHeaders: {
    display: "flex",
    backgroundColor: "#f5f5f5",
    borderBottom: "1px solid #e0e0e0",
    padding: "8px 4px",
    fontSize: "10px",
    fontWeight: 600,
    color: grayColor[6],
    flexShrink: 0,
    position: "sticky",
    top: 0,
    zIndex: 5,
  },
  columnLabel: {
    flex: "0 0 180px",
    paddingLeft: "8px",
    textAlign: "left",
    cursor: "pointer",
    userSelect: "none",
    "&:hover": {
      color: grayColor[2],
    },
  },
  columnHeader: {
    flex: "1 1 80px",
    textAlign: "right",
    paddingRight: "8px",
    cursor: "pointer",
    userSelect: "none",
    "&:hover": {
      color: grayColor[2],
    },
  },
  sortIndicator: {
    marginLeft: "2px",
    fontSize: "8px",
  },
  plOuterWrapper: {
    width: "100%",
    padding: "4px 0",
    flex: 1,
    display: "block",
    fontSize: "11px",
    color: grayColor[6],
    whiteSpace: "nowrap",
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "3px",
      "&:hover": {
        background: "#555",
      },
    },
  },
  plLine: {
    display: "flex",
    alignItems: "center",
    padding: "4px 4px",
    borderBottom: "1px solid #f5f5f5",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "#fafafa",
    },
  },
  plLineLabel: {
    flex: "0 0 180px",
    display: "flex",
    alignItems: "center",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  plValue: {
    flex: "1 1 80px",
    textAlign: "right",
    paddingRight: "8px",
  },
  plDrillDown: {
    cursor: "pointer",
  },
  drillDownIcon: {
    display: "inline-block",
    marginRight: "4px",
    fontSize: "8px",
    transition: "transform 0.2s",
    width: "12px",
    textAlign: "center",
  },
  plDrillDownOpen: {
    transform: "rotate(90deg)",
  },
  plDrillDownClosed: {
    transform: "rotate(0deg)",
  },
  level1: {
    fontWeight: 600,
    color: grayColor[1],
    backgroundColor: "#fafafa",
  },
  level2: {
    fontWeight: 500,
    color: "#666666", // mais escuro que grayColor[3] (#AAAAAA)
  },
  level3: {
    fontWeight: 400,
    color: "#777777", // mais escuro que grayColor[5] (#DDD)
  },
  level4: {
    fontWeight: 400,
    color: grayColor[6],
    fontSize: "10px",
  },
});

const ChannelTransparencyTable = ({
  data,
  drillDownsOpen,
  setDrillDownsOpen,
  clientColor,
  viewMode = "channel",
}) => {
  const useStyles = makeStyles(() => styles());
  const classes = useStyles();

  // clientColor não é usado nos estilos, mas mantido para consistência com Pnl
  // eslint-disable-next-line no-unused-vars
  const _clientColor = clientColor;

  // Estado de ordenação: key = coluna, direction = 'asc' ou 'desc'
  const [sortConfig, setSortConfig] = useState({
    key: "volume",
    direction: "desc",
  });

  // Handler para clique no header - alterna direção ou muda coluna
  const handleSortClick = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // Mesma coluna: inverte direção
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      // Nova coluna: default desc para métricas, asc para label
      return { key, direction: key === "label" ? "asc" : "desc" };
    });
  };

  // Lógica de drill-down
  const handleDrillDownClick = (rowId) => {
    if (drillDownsOpen.includes(rowId)) {
      setDrillDownsOpen(drillDownsOpen.filter((id) => id !== rowId));
    } else {
      setDrillDownsOpen([...drillDownsOpen, rowId]);
    }
  };

  /**
   * Transforma os dados baseado no viewMode.
   * - 'channel': usa dados originais (Channel > Client > Brand > Product)
   * - 'client': filtra L1 (Channel) e ajusta níveis (Client vira L1, Brand vira L2, Product vira L3)
   */
  const getTransformedData = () => {
    if (viewMode === "channel") {
      return data.rows;
    }

    // Modo 'client': filtrar apenas níveis >= 2 e ajustar
    return data.rows
      .filter((row) => row.level >= 2)
      .map((row) => ({
        ...row,
        // Ajustar nível visual: L2->L1, L3->L2, L4->L3
        level: row.level - 1,
        // Ajustar parent: se era L2 (agora L1), não tem mais parent
        parent: row.level === 2 ? null : row.parent,
        // Ajustar is_parent: L4 (agora L3) continua sem filhos
        is_parent: row.level === 2 ? row.is_parent : row.is_parent,
      }));
  };

  /**
   * Ordena as rows hierarquicamente pelo critério selecionado.
   * Mantém a estrutura pai-filho, mas ordena dentro de cada grupo.
   */
  const getSortedRows = useMemo(() => {
    const transformedRows = getTransformedData();
    const { key, direction } = sortConfig;

    // Função de comparação
    const compare = (a, b) => {
      let aVal, bVal;

      if (key === "label") {
        aVal = a.label?.toLowerCase() || "";
        bVal = b.label?.toLowerCase() || "";
      } else {
        aVal = a.metrics?.[key] ?? 0;
        bVal = b.metrics?.[key] ?? 0;
      }

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    };

    // Agrupa rows por parent
    const rowsByParent = {};
    transformedRows.forEach((row) => {
      const parentKey = row.parent || "__root__";
      if (!rowsByParent[parentKey]) {
        rowsByParent[parentKey] = [];
      }
      rowsByParent[parentKey].push(row);
    });

    // Ordena cada grupo
    Object.keys(rowsByParent).forEach((parentKey) => {
      rowsByParent[parentKey].sort(compare);
    });

    // Reconstrói o array na ordem hierárquica
    const result = [];
    const appendChildren = (parentId) => {
      const children = rowsByParent[parentId || "__root__"] || [];
      children.forEach((child) => {
        result.push(child);
        if (child.is_parent) {
          appendChildren(child.id);
        }
      });
    };

    appendChildren(null);
    return result;
  }, [data.rows, viewMode, sortConfig]);

  // Filtrar linhas visíveis baseado nos parents expandidos
  const getVisibleRows = () => {
    const visible = [];

    getSortedRows.forEach((row) => {
      // Nível 1 sempre visível
      if (row.level === 1) {
        visible.push(row);
        return;
      }

      // Outros níveis: verificar se todos os parents estão expandidos
      let parent = row.parent;
      let isVisible = true;

      while (parent) {
        if (!drillDownsOpen.includes(parent)) {
          isVisible = false;
          break;
        }
        const parentRow = getSortedRows.find((r) => r.id === parent);
        parent = parentRow?.parent;
      }

      if (isVisible) {
        visible.push(row);
      }
    });

    return visible;
  };

  // Formatar valor
  const formatValue = (value, format) => {
    if (value === null || value === undefined) return "-";

    switch (format) {
      case "number":
        return value.toLocaleString("en-US");
      case "currency":
        return value.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      case "decimal":
        return value.toFixed(1);
      case "percent":
        return `${value.toFixed(1)}%`;
      default:
        return value;
    }
  };

  // Renderizar linha
  const renderRow = (row) => {
    const isExpanded = drillDownsOpen.includes(row.id);
    const indent = row.level * 12; // 12px por nível

    return (
      <Box
        key={row.id}
        className={cx(
          classes.plLine,
          classes[`level${row.level}`],
          row.is_parent && classes.plDrillDown,
        )}
        onClick={() => row.is_parent && handleDrillDownClick(row.id)}
      >
        {/* Label com indentação */}
        <Box
          className={classes.plLineLabel}
          style={{ paddingLeft: `${indent}px` }}
        >
          {row.is_parent && (
            <span
              className={cx(
                classes.drillDownIcon,
                isExpanded
                  ? classes.plDrillDownOpen
                  : classes.plDrillDownClosed,
              )}
            >
              ▶
            </span>
          )}
          {!row.is_parent && (
            <span style={{ width: "12px", display: "inline-block" }} />
          )}
          <span>{row.label}</span>
        </Box>

        {/* Colunas de métricas */}
        {data.columns.map((col) => (
          <Box key={col.key} className={classes.plValue}>
            {formatValue(row.metrics[col.key], col.format)}
          </Box>
        ))}
      </Box>
    );
  };

  // Renderiza o indicador de ordenação
  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return (
      <span className={classes.sortIndicator}>
        {sortConfig.direction === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  return (
    <Box className={classes.plWrapper}>
      {/* Cabeçalho de colunas - clicável para ordenação */}
      <Box className={classes.columnHeaders}>
        <Box
          className={classes.columnLabel}
          onClick={() => handleSortClick("label")}
        >
          Grupo de Clientes{renderSortIndicator("label")}
        </Box>
        {data.columns.map((col) => (
          <Box
            key={col.key}
            className={classes.columnHeader}
            onClick={() => handleSortClick(col.key)}
          >
            {col.label}
            {renderSortIndicator(col.key)}
          </Box>
        ))}
      </Box>

      {/* Conteúdo scrollável */}
      <Box className={classes.plOuterWrapper}>
        {getVisibleRows().map((row) => renderRow(row))}
      </Box>
    </Box>
  );
};

ChannelTransparencyTable.propTypes = {
  data: PropTypes.object.isRequired,
  drillDownsOpen: PropTypes.array.isRequired,
  setDrillDownsOpen: PropTypes.func.isRequired,
  clientColor: PropTypes.string.isRequired,
  viewMode: PropTypes.oneOf(["channel", "client"]),
};

export default ChannelTransparencyTable;
